import React, { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, RotateCcw, Brain, User, Calendar, Award, MessageSquare, AlertCircle, FileText, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const ManualGradingModal = ({ isOpen, onClose, submission, onSuccess }) => {
    const [questionScores, setQuestionScores] = useState({});
    const [totalScore, setTotalScore] = useState(0);
    const [feedback, setFeedback] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [quiz, setQuiz] = useState({ questions: [] });
    const [answers, setAnswers] = useState({});

    useEffect(() => {
        if (submission && submission.content) {
            try {
                const parsedQuiz = JSON.parse(submission.content);
                setQuiz(parsedQuiz);

                // Initialize scores for each question
                const initialScores = {};
                parsedQuiz.questions?.forEach((q, index) => {
                    initialScores[index] = 0;
                });
                setQuestionScores(initialScores);
            } catch (e) {
                console.error('Error parsing quiz content:', e);
            }
        }

        if (submission && submission.response_data) {
            try {
                const parsedResponse = JSON.parse(submission.response_data);
                setAnswers(parsedResponse.answers || {});
            } catch (e) {
                console.error('Error parsing response data:', e);
            }
        }
    }, [submission]);

    // Calculate total score whenever question scores change
    useEffect(() => {
        const totalPoints = quiz.questions?.reduce((sum, q) => sum + (q.points || 1), 0) || 0;
        const earnedPoints = Object.values(questionScores).reduce((sum, score) => sum + (score || 0), 0);
        const percentage = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0;
        setTotalScore(percentage);
    }, [questionScores, quiz.questions]);

    const handleScoreChange = (questionIndex, score, maxPoints) => {
        const clampedScore = Math.min(Math.max(0, score), maxPoints);
        setQuestionScores(prev => ({
            ...prev,
            [questionIndex]: clampedScore
        }));
    };

    const handleAction = async (action) => {
        setIsSubmitting(true);
        const loadingToast = toast.loading(
            action === 'approve' ? 'Approving submission...' :
                action === 'reject' ? 'Rejecting submission...' :
                    'Sending back for resubmission...'
        );

        try {
            const token = localStorage.getItem('token');
            await api.post(
                `/tasks/${submission.id}/review`,
                {
                    action,
                    score: totalScore,
                    feedback
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            toast.success(
                action === 'approve' ? 'Submission approved!' :
                    action === 'reject' ? 'Submission rejected' :
                        'Sent back for resubmission',
                { id: loadingToast }
            );

            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error reviewing submission:', error);
            toast.error('Failed to process review', { id: loadingToast });
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (!isOpen || !submission) return null;

    const totalPoints = quiz.questions?.reduce((sum, q) => sum + (q.points || 1), 0) || 0;
    const earnedPoints = Object.values(questionScores).reduce((sum, score) => sum + (score || 0), 0);
    const passingScore = submission.passing_score || 70;
    const hasPassed = totalScore >= passingScore;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4">
            <div className="bg-white rounded-none md:rounded-xl shadow-2xl max-w-[95vw] md:max-w-7xl w-full h-full md:h-[90vh] overflow-y-auto flex flex-col">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 md:px-6 md:py-4 z-10 flex-shrink-0">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 md:gap-4 flex-1">
                            <div className="p-2 md:p-3 rounded-lg bg-purple-100 text-purple-700 flex-shrink-0">
                                <Brain className="w-5 h-5 md:w-6 md:h-6" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-1 truncate">
                                    Grade Quiz Submission
                                </h2>
                                <p className="text-xs md:text-sm text-gray-600 truncate">{submission.title}</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                        >
                            <X className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 md:p-6 space-y-4 md:space-y-6 flex-1 overflow-y-auto">
                    {/* Employee Info */}
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 md:p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                            <div className="flex items-center gap-3">
                                <User className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                                <div>
                                    <p className="text-xs text-primary">Employee</p>
                                    <p className="font-semibold text-gray-800 text-sm md:text-base">
                                        {submission.first_name} {submission.last_name}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Calendar className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                                <div>
                                    <p className="text-xs text-primary">Submitted</p>
                                    <p className="font-semibold text-gray-800 text-sm md:text-base">
                                        {submission.submitted_at ? formatDate(submission.submitted_at) : 'N/A'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Award className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                                <div>
                                    <p className="text-xs text-primary">Passing Score</p>
                                    <p className="font-semibold text-gray-800 text-sm md:text-base">{passingScore}%</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Proof & Signature (if applicable) */}
                    {(submission.proof_files || submission.proof_url || submission.signature_data) && (
                        <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3 md:p-4">
                            <h3 className="text-sm font-semibold text-blue-900 mb-3">Submission Evidence</h3>
                            <div className="flex flex-wrap gap-4 md:gap-6">
                                {/* Proof Files */}
                                {(submission.proof_files || submission.proof_url) && (
                                    <div className="flex-1 min-w-[200px]">
                                        <p className="text-xs font-medium text-blue-700 mb-2">Proof of Completion</p>
                                        {submission.proof_files && (() => {
                                            const files = typeof submission.proof_files === 'string'
                                                ? JSON.parse(submission.proof_files)
                                                : submission.proof_files;
                                            return files.length > 0 && (
                                                <div className="flex flex-col gap-2">
                                                    {files.map((file, idx) => (
                                                        <button
                                                            key={idx}
                                                            onClick={() => window.open(file.file_url, '_blank')}
                                                            className="flex items-center gap-2 p-2 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-left"
                                                        >
                                                            <div className="p-1.5 bg-blue-100 rounded text-blue-600">
                                                                <FileText className="w-4 h-4" />
                                                            </div>
                                                            <span className="text-sm text-gray-700 truncate flex-1">{file.file_name}</span>
                                                            <Eye className="w-4 h-4 text-gray-400" />
                                                        </button>
                                                    ))}
                                                </div>
                                            );
                                        })()}
                                        {submission.proof_url && (
                                            <div className="mt-2">
                                                <a
                                                    href={submission.proof_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                    View External Proof Link
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Signature */}
                                {submission.signature_data && (
                                    <div className="flex-1 min-w-[200px]">
                                        <p className="text-xs font-medium text-blue-700 mb-2">Signature</p>
                                        <div className="bg-white border border-blue-200 rounded-lg p-3 text-center">
                                            {(submission.signature_data.startsWith('http') || submission.signature_data.startsWith('data:')) ? (
                                                <img
                                                    src={submission.signature_data}
                                                    alt="Signature"
                                                    className="max-h-24 max-w-full object-contain mx-auto"
                                                />
                                            ) : (
                                                <p className="text-xl font-signature text-gray-800 py-2">
                                                    {submission.signature_data}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Questions and Answers */}
                    <div className="space-y-4 md:space-y-6">
                        <h3 className="font-semibold text-gray-800 text-base md:text-lg">Employee Answers</h3>

                        {quiz.questions?.length === 0 ? (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                                <AlertCircle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                                <p className="text-yellow-800">No quiz questions found</p>
                            </div>
                        ) : (
                            quiz.questions?.map((question, qIndex) => {
                                const answer = answers[qIndex] || '';
                                const maxPoints = question.points || 1;
                                const currentScore = questionScores[qIndex] || 0;

                                return (
                                    <div
                                        key={qIndex}
                                        className="bg-white border border-gray-200 rounded-lg p-4 md:p-5 shadow-sm"
                                    >
                                        {/* Question */}
                                        <div className="flex items-start gap-3 mb-4">
                                            <span className="flex items-center justify-center w-6 h-6 md:w-8 md:h-8 bg-purple-100 text-purple-700 rounded-full font-semibold text-xs md:text-sm flex-shrink-0">
                                                {qIndex + 1}
                                            </span>
                                            <div className="flex-1">
                                                <p className="text-gray-900 font-medium mb-1 text-sm md:text-base">
                                                    {question.question}
                                                </p>
                                                <span className="text-xs text-gray-500">
                                                    Max {maxPoints} point(s)
                                                </span>
                                            </div>
                                        </div>

                                        {/* Employee's Answer */}
                                        <div className="ml-0 md:ml-11 mb-4">
                                            <label className="text-sm font-medium text-gray-600 mb-2 block">
                                                Employee's Answer:
                                            </label>
                                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 md:p-4 text-gray-800 text-sm md:text-base">
                                                {answer ? (
                                                    <p className="whitespace-pre-wrap">{answer}</p>
                                                ) : (
                                                    <p className="text-gray-400 italic">No answer provided</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Score Input */}
                                        <div className="ml-0 md:ml-11 flex flex-wrap items-center gap-3 md:gap-4 bg-primary/5 border border-primary/20 rounded-lg p-3">
                                            <label className="text-sm font-medium text-primary w-full sm:w-auto">
                                                Score for this question:
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    value={currentScore}
                                                    onChange={(e) => handleScoreChange(qIndex, parseFloat(e.target.value) || 0, maxPoints)}
                                                    min="0"
                                                    max={maxPoints}
                                                    step="0.5"
                                                    className="w-16 md:w-20 px-2 py-1 md:px-3 md:py-2 border border-primary/30 rounded-lg text-center font-semibold focus:ring-2 focus:ring-primary focus:border-transparent text-sm md:text-base"
                                                />
                                                <span className="text-primary font-medium text-sm md:text-base">/ {maxPoints}</span>
                                            </div>
                                            {/* Quick score buttons */}
                                            <div className="flex gap-1 ml-auto">
                                                <button
                                                    type="button"
                                                    onClick={() => handleScoreChange(qIndex, 0, maxPoints)}
                                                    className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                                >
                                                    0
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleScoreChange(qIndex, maxPoints / 2, maxPoints)}
                                                    className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
                                                >
                                                    Half
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleScoreChange(qIndex, maxPoints, maxPoints)}
                                                    className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                                >
                                                    Full
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Total Score Summary */}
                    <div className={`border-2 rounded-lg p-4 md:p-6 ${hasPassed ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-1">Total Score</h3>
                                <p className="text-sm text-gray-600">
                                    {earnedPoints} / {totalPoints} points
                                </p>
                            </div>
                            <div className="text-right">
                                <p className={`text-3xl md:text-4xl font-bold ${hasPassed ? 'text-green-700' : 'text-red-700'}`}>
                                    {totalScore}%
                                </p>
                                <p className={`text-sm font-medium ${hasPassed ? 'text-green-600' : 'text-red-600'}`}>
                                    {hasPassed ? 'Passing' : 'Below Passing'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Feedback */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                            <MessageSquare className="w-4 h-4" />
                            Feedback for Employee (Optional)
                        </label>
                        <textarea
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            placeholder="Provide feedback on the employee's answers..."
                            rows="4"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm md:text-base"
                        />
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-4 py-3 md:px-6 md:py-4 flex flex-col md:flex-row gap-3 justify-between flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="w-full md:w-auto px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors order-2 md:order-1"
                    >
                        Cancel
                    </button>

                    <div className="flex flex-col md:flex-row gap-3 order-1 md:order-2">
                        <button
                            onClick={() => handleAction('resubmit')}
                            disabled={isSubmitting}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors disabled:opacity-50 text-sm md:text-base"
                        >
                            <RotateCcw className="w-4 h-4" />
                            Request Resubmission
                        </button>
                        <div className="flex gap-3">
                            <button
                                onClick={() => handleAction('reject')}
                                disabled={isSubmitting}
                                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm md:text-base"
                            >
                                <XCircle className="w-4 h-4" />
                                Reject
                            </button>
                            <button
                                onClick={() => handleAction('approve')}
                                disabled={isSubmitting}
                                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm md:text-base"
                            >
                                <CheckCircle className="w-4 h-4" />
                                Approve
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManualGradingModal;
